import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { AbstractControl, NonNullableFormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { DatePickerModule } from "@openng/optimus-ui/datepicker";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { MultiSelectModule } from "@openng/optimus-ui/multiselect";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { RadioButtonModule } from "@openng/optimus-ui/radiobutton";
import { SelectModule } from "@openng/optimus-ui/select";
import { DateTime } from "luxon";
import { first } from "rxjs";
import { PERIOD_QUICK_SELECTIONS, PeriodQuickSelection } from "../../../../constants/periodQuickSelections";
import { DropdownOption } from "../../../../models/ui/dropdownOption";
import {
  EnergyDataExportRequest,
  EnergyExportService,
} from "../../../../services/api/energyExport/energy-export.service";
import { ButtonComponent } from "../../../../shared/components/button/button.component";

export type EnergyDataExportKey = "energy_loss" | "normal_energy_loss";
type EnergyDataExportDateMode = "period" | "quick-selection";

interface EnergyDataExportOption extends DropdownOption {
  value: EnergyDataExportKey;
}

interface EnergyDataExportDialogData {
  assetId: string;
  assetType: string;
}

interface EnergyDataExportFormValue {
  keys: EnergyDataExportKey[];
  dateMode: EnergyDataExportDateMode;
  startDate: Date;
  endDate: Date;
  quickSelection: PeriodQuickSelection;
}

@Component({
  selector: "app-energy-data-export-dialog",
  standalone: true,
  imports: [
    ButtonComponent,
    DatePickerModule,
    MultiSelectModule,
    ProgressSpinnerModule,
    RadioButtonModule,
    ReactiveFormsModule,
    SelectModule,
    TranslateModule,
  ],
  templateUrl: "./energy-data-export-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: "./energy-data-export-dialog.component.scss",
})
export class EnergyDataExportDialogComponent {
  private readonly config = inject(DynamicDialogConfig);
  private readonly energyExportService = inject(EnergyExportService);
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly ref = inject(DynamicDialogRef);

  private readonly dialogData = this.config.data as Partial<EnergyDataExportDialogData>;

  protected readonly loading = signal(false);
  protected readonly dataOptions: EnergyDataExportOption[] = [
    { label: "energy_tool.energy_data_export.energy_loss", value: "energy_loss" },
    { label: "energy_tool.energy_data_export.normal_energy_loss", value: "normal_energy_loss" },
  ];
  protected readonly quickSelectionOptions = [...PERIOD_QUICK_SELECTIONS];
  public readonly exportForm = this.formBuilder.group(
    {
      keys: this.formBuilder.control<EnergyDataExportKey[]>(["energy_loss"], [this.atLeastOneSelected()]),
      dateMode: this.formBuilder.control<EnergyDataExportDateMode>("quick-selection", [Validators.required]),
      startDate: this.formBuilder.control(DateTime.now().startOf("day").toJSDate(), [Validators.required]),
      endDate: this.formBuilder.control(DateTime.now().startOf("day").toJSDate(), [Validators.required]),
      quickSelection: this.formBuilder.control<PeriodQuickSelection>("this_month", [Validators.required]),
    },
    { validators: this.periodDateRangeValidator() }
  );

  /**
   * Closes the dialog without exporting data.
   */
  public closeDialog(): void {
    this.ref.close();
  }

  protected getDataOptionLabelKey(selectedOption: EnergyDataExportKey | EnergyDataExportOption): string {
    if (typeof selectedOption !== "string") {
      return selectedOption.label;
    }

    return this.dataOptions.find((option) => option.value === selectedOption)?.label ?? selectedOption;
  }

  protected getDataOptionValue(selectedOption: EnergyDataExportKey | EnergyDataExportOption): EnergyDataExportKey {
    return typeof selectedOption === "string" ? selectedOption : selectedOption.value;
  }

  /**
   * Creates the energy data export request from the current dialog form value.
   */
  public createExportRequest(): EnergyDataExportRequest | null {
    const dateRange = this.resolveDateRange(this.exportForm.getRawValue());
    const assetId = this.dialogData.assetId;
    const assetType = this.dialogData.assetType;
    if (!dateRange || !assetId || !assetType) {
      return null;
    }

    return {
      assetId,
      assetType,
      from: dateRange.from,
      to: dateRange.to,
      aggregation: dateRange.from === dateRange.to ? "hour" : "day",
      keys: this.exportForm.controls.keys.getRawValue().join(","),
    };
  }

  /**
   * Starts the energy data export and downloads the returned file after a successful response.
   */
  public startExport(): void {
    if (this.exportForm.invalid) {
      this.exportForm.markAllAsTouched();
      return;
    }

    const request = this.createExportRequest();
    if (!request) {
      return;
    }

    this.loading.set(true);
    this.energyExportService
      .getEnergyDataExport(request)
      .pipe(first())
      .subscribe({
        next: (response) => {
          if (response.status === 200 && response.body) {
            const contentDisposition = response.headers.get("content-disposition");
            const filename = this.getFilenameFromContentDisposition(contentDisposition) ?? "energy_data_export.csv";
            this.downloadEnergyData(response.body, filename);
            this.ref.close(true);
          }
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  private getFilenameFromContentDisposition(contentDisposition: string | null): string | null {
    if (!contentDisposition) {
      return null;
    }

    const match = contentDisposition.match(/filename="([^"]+)"/i);
    return match?.[1] ?? null;
  }

  private resolveDateRange(value: EnergyDataExportFormValue): { from: string; to: string } | null {
    const range =
      value.dateMode === "period"
        ? this.resolvePeriodDateRange(value.startDate, value.endDate)
        : this.resolveQuickSelectionDateRange(value.quickSelection);
    const from = range.from.toISODate();
    const to = range.to.toISODate();

    if (!from || !to || range.from > range.to) {
      return null;
    }

    return { from, to };
  }

  private resolvePeriodDateRange(startDate: Date, endDate: Date): { from: DateTime; to: DateTime } {
    return {
      from: DateTime.fromJSDate(startDate).startOf("day"),
      to: DateTime.fromJSDate(endDate).startOf("day"),
    };
  }

  private resolveQuickSelectionDateRange(selection: PeriodQuickSelection): { from: DateTime; to: DateTime } {
    const now = DateTime.now();
    switch (selection) {
      case "today":
        return { from: now.startOf("day"), to: now.startOf("day") };
      case "yesterday":
        return {
          from: now.minus({ days: 1 }).startOf("day"),
          to: now.minus({ days: 1 }).startOf("day"),
        };
      case "day_before_yesterday":
        return {
          from: now.minus({ days: 2 }).startOf("day"),
          to: now.minus({ days: 2 }).startOf("day"),
        };
      case "this_week":
        return { from: now.startOf("week"), to: now.endOf("week") };
      case "last_week":
        return {
          from: now.minus({ weeks: 1 }).startOf("week"),
          to: now.minus({ weeks: 1 }).endOf("week"),
        };
      case "this_month":
        return { from: now.startOf("month"), to: now.endOf("month") };
      case "last_month":
        return {
          from: now.minus({ months: 1 }).startOf("month"),
          to: now.minus({ months: 1 }).endOf("month"),
        };
      case "this_year":
        return { from: now.startOf("year"), to: now.endOf("year") };
      case "last_year":
        return {
          from: now.minus({ years: 1 }).startOf("year"),
          to: now.minus({ years: 1 }).endOf("year"),
        };
    }
  }

  private downloadEnergyData(exportBlob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(exportBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private atLeastOneSelected(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      return Array.isArray(value) && value.length > 0 ? null : { required: true };
    };
  }

  private periodDateRangeValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.get("dateMode")?.value !== "period") {
        return null;
      }

      const startDate = control.get("startDate")?.value;
      const endDate = control.get("endDate")?.value;
      if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
        return { invalidDateRange: true };
      }

      const start = DateTime.fromJSDate(startDate).startOf("day");
      const end = DateTime.fromJSDate(endDate).startOf("day");
      return start.isValid && end.isValid && start <= end ? null : { invalidDateRange: true };
    };
  }
}
